import '@atoms/PickerCombobox/PickerCombobox.scss';
import './AbookCombobox.scss';
import React, { FC, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { SegmentedControl } from '@mantine/core';
import { MdCheck, MdOutlineOpenInNew, MdSearch } from 'react-icons/md';
import Button from '@srm/ui/button';
import StatusTag from '@srm/ui/status-tag';
import dayjs from '@srm/ui/lib/dayjs';
import { toLocalDate } from '@srm/ui/lib/toLocalDate';
import { highlightMatches } from '@srm/ui/lib/highlightMatches';
import { useOptionalUsername } from '@hooks/useOptionalUsername';
import { AddressBookService } from '@services/AddressBookService';
import { IAbookTab } from '@models/Forms/IForms';
import { IAddressBookData } from '@models/addressbook/IAddressBookData';
import { abookItemTypeLabel, isAbookPerson } from '@models/addressbook/IPersonCard';
import { capitalize } from '@utils/capitalize';
import { usePickerNavigation } from '@atoms/PickerCombobox/usePickerNavigation';
import PickerSkeleton from '@atoms/PickerCombobox/PickerSkeleton';
import { pickerFooterText } from '@atoms/PickerCombobox/pickerFooterText';
import PickerDependenciesHint from '@atoms/PickerCombobox/PickerDependenciesHint';
import AbookItemAvatar from './AbookItemAvatar';
import { isPersonTab } from '@utils/abookTabVisibility';
import { fetchTabItemsByKeys, useAbookComboboxItems } from './useAbookComboboxItems';
import { readAbookRecentKeys, rememberAbookRecentKeys } from './abookRecentKeys';

export interface IAbookComboboxProps {
    /** Вкладки-списки поля (дерево выбирается только в окне адресной книги). */
    tabs: IAbookTab[];
    isMultiple: boolean;
    /** Ключи текущего значения поля. */
    selected?: number[];
    title: string;
    /** Значения формы для условий вкладок: вычисляются один раз при открытии. */
    getFormValues: () => string;
    /** Символ, напечатанный на закрытом поле, — начало поиска. */
    initialQuery?: string;
    /** Подписи пустых полей формы, от которых зависят вкладки (`formValues`). */
    getEmptyDependencies?: () => string[];
    onSubmit: (items: IAddressBookData[]) => Promise<void>;
    onClose: () => void;
    /** Окно адресной книги с вкладками, деревом и фильтрами. */
    onOpenBook?: () => void;
}

/** Строка без данных строки списка (ключ из значения поля): `id` — тот же ключ, его ждёт смена значения поля. */
const withId = (item: IAddressBookData): IAddressBookData => ({ ...item, id: item.id || String(item.key) });

const itemKey = (item: IAddressBookData) => String(item.key);
const itemTitle = (item: IAddressBookData) => item.fullName || item.name;

const shortDate = (iso?: string) => (iso ? dayjs(toLocalDate(iso)).format('DD.MM') : '');

/**
 * Выбор из адресной книги в выпадающем списке (`isSelectMode`): поиск в шапке, вкладки-списки поля сегментами,
 * строка «аватар · ФИО · должность и подразделение», отпуск с выбором ВРИО, недавние, клавиатура и выход в окно
 * адресной книги (Ctrl+Enter). Уволенных и отключённых сервер в выборку не отдаёт.
 */
const AbookCombobox: FC<IAbookComboboxProps> = (p) => {
    const username = useOptionalUsername();
    const [recentKeys] = useState(() => readAbookRecentKeys(username));
    const [formValues] = useState(p.getFormValues);
    const [tabIndex, setTabIndex] = useState(0);
    const tab = p.tabs[Math.min(tabIndex, p.tabs.length - 1)];
    const [query, setQuery] = useState(p.initialQuery ?? '');
    const items = useAbookComboboxItems(tab, formValues, query, recentKeys);
    const [selectedKeys, setSelectedKeys] = useState<string[]>(() => (p.selected ?? []).map(String));
    // ВРИО, которого нет в списке вкладки: кнопка выбора у его строки сменяется пометкой
    const [dummyMissing, setDummyMissing] = useState<string[]>([]);
    const knownItems = useRef(new Map<string, IAddressBookData>());
    const submitting = useRef(false);
    // клики множественного выбора применяются по очереди: следующий видит результат предыдущего
    const multiQueue = useRef<Promise<void>>(Promise.resolve());
    const selectedRef = useRef(selectedKeys);
    const dummyPending = useRef(false);
    const mounted = useRef(true);
    useEffect(
        () => () => {
            mounted.current = false;
        },
        [],
    );
    selectedRef.current = selectedKeys;
    const listId = useId();

    useEffect(() => setSelectedKeys((p.selected ?? []).map(String)), [p.selected]);

    const trimmed = query.trim();
    const ready = items.status === 'ready';
    const showRecent = !trimmed && items.recent.length > 0;
    const person = isPersonTab(tab);

    const options = useMemo(() => {
        if (!showRecent) return items.rows;
        // недавние не повторяются в общем списке: два пункта с одним именем путают и чтение, и выбор
        const recent = new Set(items.recent.map(itemKey));
        return [...items.recent, ...items.rows.filter((row) => !recent.has(itemKey(row)))];
    }, [items.recent, items.rows, showRecent]);

    const { active, setActive, searchRef, listRef, onKeyDown } = usePickerNavigation({
        count: options.length,
        resetKey: `${tabIndex}\n${trimmed}`,
        pending: items.pending,
        onChoose: (index) => void choose(options[index]),
        onOpenMore: p.onOpenBook,
        onClose: p.onClose,
    });

    useLayoutEffect(() => {
        searchRef.current?.focus({ preventScroll: true });
    }, [searchRef]);

    /**
     * Строки для отправки множественного выбора. Недостающие — сначала из вкладки: у её строки есть ФИО, должность и
     * атрибуты для setValues; не нашлось (значение из другой вкладки) — краткие данные адресной книги по ключу.
     */
    const resolveItems = async (keys: string[]) => {
        [...items.rows, ...items.recent].forEach((row) => knownItems.current.set(itemKey(row), row));
        let missing = keys.filter((key) => !knownItems.current.has(key));
        if (missing.length) {
            (await fetchTabItemsByKeys(tab, formValues, missing)).forEach((row) =>
                knownItems.current.set(itemKey(row), row),
            );
            missing = keys.filter((key) => !knownItems.current.has(key));
        }
        if (missing.length) {
            const response = await AddressBookService.fetchAbookItemsByKeys(missing);
            (response.data ?? []).forEach((row) => knownItems.current.set(itemKey(row), row));
        }
        return keys.flatMap((key) => {
            const item = knownItems.current.get(key);
            return item ? [withId(item)] : [];
        });
    };

    const toggleMultiple = (item: IAddressBookData) => {
        multiQueue.current = multiQueue.current.then(async () => {
            const before = selectedRef.current;
            const key = itemKey(item);
            const adding = !before.includes(key);
            const next = adding ? [...before, key] : before.filter((k) => k !== key);
            knownItems.current.set(key, item);
            setSelectedKeys(next);
            selectedRef.current = next;
            try {
                await p.onSubmit(await resolveItems(next));
                if (adding) rememberAbookRecentKeys(username, [key]);
            } catch (error) {
                // значение формы не изменилось — отметки возвращаются к нему
                setSelectedKeys(before);
                selectedRef.current = before;
                console.error(error);
            }
        });
    };

    const choose = async (item: IAddressBookData) => {
        if (p.isMultiple) {
            toggleMultiple(item);
            return;
        }
        if (submitting.current) return;
        submitting.current = true;
        try {
            rememberAbookRecentKeys(username, [itemKey(item)]);
            await p.onSubmit([withId(item)]);
            p.onClose();
        } finally {
            submitting.current = false;
        }
    };

    /**
     * ВРИО выбирается, только если проходит условия вкладки — как любой пункт списка. Пока его ищем, повторный
     * выбор ВРИО не начинается, а ответ после закрытия списка (выбран другой, Escape) значение уже не меняет.
     */
    const chooseDummy = async (item: IAddressBookData) => {
        if (dummyPending.current) return;
        dummyPending.current = true;
        const dummyKey = item.dummyKey!;
        try {
            const [dummy] = await fetchTabItemsByKeys(tab, formValues, [dummyKey]);
            if (!mounted.current) return;
            if (!dummy) {
                setDummyMissing((keys) => [...keys, dummyKey]);
            } else if (!(p.isMultiple && selectedRef.current.includes(itemKey(dummy)))) {
                // уже отмеченный ВРИО не снимается: «Выбрать ВРИО» только добавляет
                await choose(dummy);
            }
        } catch (error) {
            console.error(error);
        } finally {
            dummyPending.current = false;
        }
    };

    /** ВРИО выбирается у сотрудника в отпуске, если замещающий не оказался вне вкладки. */
    const canChooseDummy = (item: IAddressBookData) =>
        isAbookPerson(item) && !!item.isVacation && !!item.dummyKey && !dummyMissing.includes(item.dummyKey);

    // Alt+Enter — ВРИО подсвеченного отпускника: кнопка в строке мышью, а Tab закрывает список
    const onComboboxKeyDown = (e: React.KeyboardEvent) => {
        const item = options[active];
        if (e.key === 'Enter' && e.altKey && item && canChooseDummy(item)) {
            e.preventDefault();
            void chooseDummy(item);
            return;
        }
        // Alt+←/→ — соседняя вкладка: до переключателя вкладок Tab не дойдёт, он закрывает список
        if (e.altKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight') && p.tabs.length > 1) {
            e.preventDefault();
            const step = e.key === 'ArrowRight' ? 1 : -1;
            setTabIndex((i) => (i + step + p.tabs.length) % p.tabs.length);
            return;
        }
        onKeyDown(e);
    };

    const optionId = (index: number) => `${listId}-option-${index}`;

    // у подразделения в department — вышестоящее подразделение
    const subtitle = (item: IAddressBookData) =>
        (isAbookPerson(item)
            ? [item.position, item.department]
            : [capitalize(abookItemTypeLabel(item.type) ?? ''), item.department]
        )
            .filter(Boolean)
            .join(' · ');

    const renderSide = (item: IAddressBookData) => {
        if (!isAbookPerson(item) || !item.isVacation) return null;
        if (canChooseDummy(item)) {
            return (
                <Button
                    size="compact"
                    variant="light"
                    className="rf-abook-combobox__dummy"
                    title={`Выбрать временно исполняющего обязанности: ${item.dummyName} (Alt+Enter)`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => {
                        e.stopPropagation();
                        void chooseDummy(item);
                    }}
                >
                    Выбрать ВРИО
                </Button>
            );
        }
        return (
            <StatusTag tone="warning" className="rf-abook-combobox__tag">
                В отпуске
            </StatusTag>
        );
    };

    const renderOption = (item: IAddressBookData, index: number, section: 'recent' | 'all') => {
        const key = itemKey(item);
        const selected = selectedKeys.includes(key);
        const sub = subtitle(item);
        const away = isAbookPerson(item) && item.isVacation && !!item.dummyKey;
        const until = shortDate(item.dummyEndDate);
        return (
            <div
                key={`${section}-${key}`}
                id={optionId(index)}
                data-index={index}
                role="option"
                tabIndex={-1}
                aria-selected={selected}
                aria-label={itemTitle(item)}
                aria-describedby={sub || away ? `${optionId(index)}-sub` : undefined}
                className={clsx('rf-picker-combobox__option', 'rf-abook-combobox__option', {
                    'rf-picker-combobox__option--active': index === active,
                    'rf-picker-combobox__option--selected': selected,
                })}
                onMouseMove={() => index !== active && setActive(index)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => void choose(item)}
            >
                {p.isMultiple && (
                    <span className="rf-picker-combobox__checkbox" aria-hidden="true">
                        {selected && <MdCheck />}
                    </span>
                )}
                <AbookItemAvatar item={item} size={32} />
                <span className="rf-picker-combobox__text">
                    <span className="rf-picker-combobox__title">
                        {highlightMatches(itemTitle(item), trimmed, 'rf-picker-combobox__mark')}
                    </span>
                    {away ? (
                        <span
                            id={`${optionId(index)}-sub`}
                            className="rf-picker-combobox__subtitle rf-abook-combobox__away"
                        >
                            В отпуске · ВРИО {item.dummyName}
                            {until && ` до ${until}`}
                        </span>
                    ) : (
                        sub && (
                            <span id={`${optionId(index)}-sub`} className="rf-picker-combobox__subtitle">
                                {highlightMatches(sub, trimmed, 'rf-picker-combobox__mark')}
                            </span>
                        )
                    )}
                </span>
                {renderSide(item)}
                {!p.isMultiple && selected && <MdCheck className="rf-picker-combobox__tick" aria-hidden="true" />}
            </div>
        );
    };

    const renderEmpty = () => {
        if (items.status === 'error') {
            return (
                <div className="rf-picker-combobox__empty" role="status">
                    <b>Не удалось загрузить адресную книгу</b>
                    <Button size="sm" variant="light" onClick={items.retry}>
                        Повторить
                    </Button>
                </div>
            );
        }
        const dependencies = trimmed ? [] : (p.getEmptyDependencies?.() ?? []);
        return (
            <div className="rf-picker-combobox__empty" role="status">
                {trimmed ? (
                    <>
                        <b>Никого не нашли по «{trimmed}»</b>
                        {person ? 'Ищем по ФИО, должности и подразделению.' : 'Ищем по наименованию.'}
                    </>
                ) : dependencies.length ? (
                    <PickerDependenciesHint labels={dependencies} />
                ) : (
                    <>
                        <b>В списке никого нет</b>
                        Список ограничен условиями вкладки.
                    </>
                )}
                {p.onOpenBook && !dependencies.length && (
                    <Button size="sm" variant="light" onClick={p.onOpenBook}>
                        Открыть адресную книгу
                    </Button>
                )}
            </div>
        );
    };

    const renderList = () => {
        // ошибка новой выдачи — даже при строках прошлой: по ним уже нельзя выбрать
        if (items.status === 'error') return renderEmpty();
        if (!ready && !options.length) return <PickerSkeleton avatar />;
        if (!options.length) return renderEmpty();
        const recentCount = showRecent ? items.recent.length : 0;
        return (
            <>
                {recentCount > 0 && (
                    <div className="rf-picker-combobox__section" role="presentation">
                        Недавние
                    </div>
                )}
                {options.slice(0, recentCount).map((item, i) => renderOption(item, i, 'recent'))}
                {recentCount > 0 && (
                    <div className="rf-picker-combobox__section" role="presentation">
                        {tab.name}
                    </div>
                )}
                {options.slice(recentCount).map((item, i) => renderOption(item, recentCount + i, 'all'))}
            </>
        );
    };

    const footerText = () =>
        pickerFooterText({
            ready,
            isMultiple: p.isMultiple,
            count: options.length,
            selected: selectedKeys.length,
            shown: items.rows.length,
            total: items.total,
            query: trimmed,
            forms: ['запись', 'записи', 'записей'],
        });

    return (
        <div className="rf-picker-combobox rf-abook-combobox" onKeyDown={onComboboxKeyDown}>
            <div className={clsx('rf-picker-combobox__search', p.tabs.length > 1 && 'rf-abook-combobox__search--tabs')}>
                <MdSearch className="rf-picker-combobox__search-icon" aria-hidden="true" />
                <input
                    ref={searchRef}
                    className="rf-picker-combobox__search-input"
                    type="text"
                    role="combobox"
                    aria-label="Поиск в адресной книге"
                    aria-controls={listId}
                    aria-expanded="true"
                    aria-autocomplete="list"
                    aria-activedescendant={options[active] ? optionId(active) : undefined}
                    placeholder={person ? 'ФИО, должность или подразделение' : 'Наименование'}
                    autoComplete="off"
                    spellCheck={false}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={(e) => e.target.setSelectionRange(e.target.value.length, e.target.value.length)}
                />
            </div>
            {p.tabs.length > 1 && (
                <div className="rf-abook-combobox__tabs">
                    <SegmentedControl
                        size="xs"
                        fullWidth
                        aria-label="Вкладки адресной книги"
                        aria-keyshortcuts="Alt+ArrowLeft Alt+ArrowRight"
                        value={String(tabIndex)}
                        onChange={(value) => {
                            setTabIndex(Number(value));
                            searchRef.current?.focus({ preventScroll: true });
                        }}
                        data={p.tabs.map((t, i) => ({ value: String(i), label: t.name }))}
                    />
                </div>
            )}
            <div
                ref={listRef}
                id={listId}
                data-testid="rf-select-list-scroll"
                role="listbox"
                aria-label={p.title}
                aria-multiselectable={p.isMultiple || undefined}
                aria-busy={!ready || items.pending || undefined}
                tabIndex={-1}
                className={clsx(
                    'rf-picker-combobox__list',
                    !ready && options.length > 0 && 'rf-picker-combobox__list--pending',
                )}
            >
                {renderList()}
            </div>
            <div className="rf-picker-combobox__footer">
                <span className="rf-picker-combobox__footer-text" aria-live="polite">
                    {footerText()}
                </span>
                {p.isMultiple && (
                    <Button size="sm" onClick={p.onClose}>
                        Готово
                    </Button>
                )}
                {p.onOpenBook && (
                    <Button
                        size="sm"
                        variant="text"
                        onClick={p.onOpenBook}
                        endAdornment={<MdOutlineOpenInNew size={16} />}
                        title="Окно адресной книги (Ctrl+Enter)"
                    >
                        Адресная книга
                    </Button>
                )}
            </div>
        </div>
    );
};

export default AbookCombobox;
